import React, { useState } from 'react';
import { X, Edit, AlertCircle, Save } from 'lucide-react';
import { Timestamp } from 'firebase/firestore';

/**
 * Modal pour modifier une facture
 */
const EditInvoiceModal = ({ isOpen, onClose, invoice, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // État du formulaire
  const [formData, setFormData] = useState({
    periodeLibelle: invoice?.periode?.libelle || '',
    periodeDebut: invoice?.periode?.debut ? formatDateForInput(invoice.periode.debut) : '',
    periodeFin: invoice?.periode?.fin ? formatDateForInput(invoice.periode.fin) : '',
    totalHeures: invoice?.calcul?.totalHeures || 0,
    nombreRendezVous: invoice?.calcul?.nombreRendezVous || 0,
    fraisMandatUnitaire: invoice?.calcul?.fraisMandatUnitaire || 0,
    notes: invoice?.notes || ''
  });

  if (!isOpen || !invoice) return null;

  // ⛔ Vérifier si la facture est payée
  const isFacturePayee = invoice.statut === 'payée';

  // Formater une date Timestamp pour un input date
  function formatDateForInput(timestamp) {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toISOString().split('T')[0];
  }

  // Calculer le montant total
  const calculerMontantTotal = () => {
    return formData.totalHeures * formData.fraisMandatUnitaire;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setError(null);

    try {
      console.log('✏️ Modification de la facture:', invoice.id);

      // Si facture payée, on ne modifie QUE les notes
      if (isFacturePayee) {
        const updates = {
          notes: formData.notes,
          dateModification: Timestamp.now()
        };

        await onConfirm(invoice, updates);
        console.log('✅ Notes de la facture payée mises à jour avec succès');
        onClose();
        return;
      }

      // Validation pour factures non payées
      if (formData.totalHeures < 0 || formData.fraisMandatUnitaire < 0) {
        throw new Error('Les valeurs ne peuvent pas être négatives');
      }

      if (formData.nombreRendezVous < 0) {
        throw new Error('Le nombre de rendez-vous ne peut pas être négatif');
      }

      if (!formData.periodeDebut || !formData.periodeFin) {
        throw new Error('Les dates de début et fin sont obligatoires');
      }

      if (new Date(formData.periodeDebut) > new Date(formData.periodeFin)) {
        throw new Error('La date de début doit être antérieure à la date de fin');
      }

      // Préparer les données de mise à jour complètes
      const updates = {
        periode: {
          libelle: formData.periodeLibelle,
          debut: Timestamp.fromDate(new Date(formData.periodeDebut)),
          fin: Timestamp.fromDate(new Date(formData.periodeFin))
        },
        calcul: {
          ...invoice.calcul,
          totalHeures: parseFloat(formData.totalHeures),
          nombreRendezVous: parseInt(formData.nombreRendezVous),
          fraisMandatUnitaire: parseFloat(formData.fraisMandatUnitaire),
          montantTotal: calculerMontantTotal()
        },
        notes: formData.notes,
        dateModification: Timestamp.now()
      };

      // Appeler la fonction de confirmation
      await onConfirm(invoice, updates);

      console.log('✅ Facture modifiée avec succès');
      onClose();

    } catch (err) {
      console.error('❌ Erreur modification facture:', err);
      setError(err.message || 'Échec de la modification');
    } finally {
      setLoading(false);
    }
  };

  const formatEuro = (montant) => {
    if (typeof montant === 'undefined' || montant === null) return '0,00 €';
    return montant.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* En-tête */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex justify-between items-center">
          <div className="flex items-center">
            <Edit className="w-6 h-6 text-blue-500 mr-3" />
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Modifier la facture
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Facture N° {invoice.id}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Contenu */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* ⛔ Blocage si facture payée */}
          {isFacturePayee && (
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-red-900 dark:text-red-100 mb-1">
                    ⛔ Modification bloquée
                  </h3>
                  <p className="text-sm text-red-800 dark:text-red-200">
                    Cette facture a été <strong>payée</strong> et ne peut plus être modifiée pour des raisons
                    comptables et légales. Seules les notes peuvent être ajoutées ou modifiées.
                  </p>
                  <p className="text-sm text-red-800 dark:text-red-200 mt-2">
                    Les champs ci-dessous sont désactivés.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Avertissement pour factures envoyées mais non payées */}
          {invoice.statut === 'envoyée' && (
            <div className="bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-500 p-4 rounded">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-orange-900 dark:text-orange-100 mb-1">
                    Attention : Facture envoyée
                  </h3>
                  <p className="text-sm text-orange-800 dark:text-orange-200">
                    Cette facture a déjà été envoyée au client. Vérifiez que les modifications sont nécessaires.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Informations client (lecture seule) */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
              Client (non modifiable)
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Nom :</span>
                <span className="text-gray-900 dark:text-white">{invoice.client?.nom || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Email :</span>
                <span className="text-gray-900 dark:text-white">{invoice.client?.email || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Période de facturation */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              Période de facturation
            </h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Libellé de la période *
              </label>
              <input
                type="text"
                value={formData.periodeLibelle}
                onChange={(e) => setFormData({ ...formData, periodeLibelle: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Ex: Janvier 2025, Semaine 3, etc."
                required
                disabled={loading || isFacturePayee}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date de début *
                </label>
                <input
                  type="date"
                  value={formData.periodeDebut}
                  onChange={(e) => setFormData({ ...formData, periodeDebut: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  required
                  disabled={loading || isFacturePayee}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date de fin *
                </label>
                <input
                  type="date"
                  value={formData.periodeFin}
                  onChange={(e) => setFormData({ ...formData, periodeFin: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  required
                  disabled={loading || isFacturePayee}
                />
              </div>
            </div>
          </div>

          {/* Détails de facturation */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 dark:text-white">
              Détails de facturation
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Total heures *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.totalHeures}
                  onChange={(e) => setFormData({ ...formData, totalHeures: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="0.00"
                  required
                  min="0"
                  disabled={loading || isFacturePayee}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nombre de rendez-vous
                </label>
                <input
                  type="number"
                  value={formData.nombreRendezVous}
                  onChange={(e) => setFormData({ ...formData, nombreRendezVous: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="0"
                  min="0"
                  disabled={loading || isFacturePayee}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Frais de mandat unitaire (€/heure) *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.fraisMandatUnitaire}
                onChange={(e) => setFormData({ ...formData, fraisMandatUnitaire: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="0.00"
                required
                min="0"
                disabled={loading || isFacturePayee}
              />
            </div>

            {/* Calcul automatique du montant total */}
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-green-900 dark:text-green-100">
                  Montant total calculé :
                </span>
                <span className="text-2xl font-bold text-green-900 dark:text-green-100">
                  {formatEuro(calculerMontantTotal())}
                </span>
              </div>
              <p className="text-xs text-green-700 dark:text-green-300 mt-2">
                = {formData.totalHeures} heures × {formatEuro(parseFloat(formData.fraisMandatUnitaire))}
              </p>
            </div>
          </div>

          {/* Notes - toujours modifiables même si payée */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Notes (optionnel) {isFacturePayee && <span className="text-green-600 dark:text-green-400">✓ Modifiable</span>}
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              rows="3"
              placeholder={isFacturePayee ? "Les notes peuvent être ajoutées même pour les factures payées..." : "Notes ou commentaires sur cette modification..."}
              disabled={loading}
            />
          </div>

          {/* Message d'erreur */}
          {error && (
            <div className="flex items-start space-x-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  Erreur lors de la modification
                </p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  {error}
                </p>
              </div>
            </div>
          )}

          {/* Information */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3" />
              <div className="text-sm text-blue-900 dark:text-blue-100">
                <p className="font-medium mb-1">Modifications enregistrées</p>
                <p className="text-blue-800 dark:text-blue-200">
                  Les modifications seront enregistrées avec la date de modification.
                  L'historique des changements sera conservé.
                </p>
              </div>
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              disabled={loading || isFacturePayee}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex items-center px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || isFacturePayee}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer les modifications
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditInvoiceModal;
